import { z } from 'zod';
import { largestRemainder } from './fpa-despesas.ts';
import { fromScaled, toScaled } from './money.ts';

/**
 * Fila do Realizado da Controladoria (ADR 0098). O pagamento registrado entra na fila sem mexer no
 * Realizado; só o Registrar do auditor aplica o lançamento, na competência do título, por empresa
 * beneficiada, centro e colaborador. O valor reconhecido é a despesa (bruto), proporcional ao que
 * foi pago, e nunca o líquido: a retenção faz parte da despesa.
 */

export const FPA_QUEUE_STATUSES = [
  'pending',
  'registered',
  'refused',
  'withdrawn',
  'sheet_checked',
] as const;
export type FpaQueueStatus = (typeof FPA_QUEUE_STATUSES)[number];

export const FPA_ACTUAL_CHANNELS = ['flow', 'sheet'] as const;
export type FpaActualChannel = (typeof FPA_ACTUAL_CHANNELS)[number];

export const FPA_RESULT_EFFECTS = ['result', 'outside_result'] as const;
export type FpaResultEffect = (typeof FPA_RESULT_EFFECTS)[number];

/** Por que um item ainda não pode ser registrado. A tela mostra o motivo; o banco também recusa. */
export type FpaQueueBlock =
  | 'not_activated'
  | 'sheet_competence'
  | 'sheet_origin'
  | 'period_closed'
  | 'rate_missing'
  | 'classification_missing'
  | 'pending_original';

/** Regra da lista de origens: par oficial (classificação vazia vale para o Tipo de Gasto todo). */
export interface FpaOriginRule {
  originId: string;
  originCode: string;
  channel: FpaActualChannel;
  expenseTypeId: string;
  classificationId: string | null;
  resultEffect: FpaResultEffect;
}

export interface FpaResolvedOrigin {
  originId: string | null;
  originCode: string | null;
  /** Vazio: a lista de origens ainda não diz de onde vem esse par. */
  channel: FpaActualChannel | null;
  resultEffect: FpaResultEffect;
}

/**
 * Origem de um par: a regra do par vale antes da regra do Tipo de Gasto. Pessoal é sempre da fonte
 * própria, com ou sem regra (sigilo): a folha não passa pela fila.
 */
export function resolveActualOrigin(
  rules: readonly FpaOriginRule[],
  pair: { expenseTypeId: string | null; classificationId: string | null },
  payroll: { isPayroll: boolean; origin: Pick<FpaOriginRule, 'originId' | 'originCode'> | null },
): FpaResolvedOrigin {
  if (payroll.isPayroll) {
    return {
      originId: payroll.origin?.originId ?? null,
      originCode: payroll.origin?.originCode ?? null,
      channel: 'sheet',
      resultEffect: 'result',
    };
  }
  const exact = rules.find(
    (r) => r.expenseTypeId === pair.expenseTypeId && r.classificationId === pair.classificationId,
  );
  const byType = rules.find(
    (r) => r.expenseTypeId === pair.expenseTypeId && r.classificationId === null,
  );
  const rule = exact ?? byType;
  if (!rule) return { originId: null, originCode: null, channel: null, resultEffect: 'result' };
  return {
    originId: rule.originId,
    originCode: rule.originCode,
    channel: rule.channel,
    resultEffect: rule.resultEffect,
  };
}

/** Linha do rateio do título: por valor (título de pedido) ou por percentual (título manual). */
export interface FpaTitleAllocationLine {
  costCenterId: string;
  costCenterCode: string;
  legalEntityId: string;
  amount?: string | null;
  percent?: string | null;
  employees?: { employeeId: string; amount: string }[];
}

export interface FpaRecognitionInput {
  /** Valor da despesa reconhecido neste pagamento, já sem a parte que liquida retenção. */
  recognized: string;
  /** Líquido pago reconhecido nesta linha do pagamento. */
  paidNet: string;
  /** Juros e multa menos desconto. */
  financial: string;
}

export interface FpaRecognitionLine {
  legalEntityId: string;
  costCenterId: string;
  employeeId: string | null;
  amount: string;
  paidNet: string;
  withheld: string;
  financial: string;
}

const splitByWeight = (
  value: bigint,
  weights: readonly { weight: bigint; tieKey: string }[],
): bigint[] => largestRemainder(value, 4, weights);

/**
 * Divide o que foi reconhecido pelo rateio do título, sem cálculo próprio: mesmo maior resto das
 * outras frentes (ADR 0092), primeiro entre os centros e depois entre as pessoas de cada centro. As
 * somas batem exatamente com os valores de entrada, e a retenção de cada linha é a diferença entre
 * a despesa e o líquido dela. Sem rateio, tudo fica no centro do título.
 */
export function recognitionLines(
  allocation: readonly FpaTitleAllocationLine[],
  fallback: Pick<FpaTitleAllocationLine, 'costCenterId' | 'costCenterCode' | 'legalEntityId'>,
  input: FpaRecognitionInput,
): FpaRecognitionLine[] {
  const lines = allocation.length ? allocation : [{ ...fallback, percent: '100' }];
  const weightOf = (l: FpaTitleAllocationLine) => toScaled(l.amount ?? l.percent ?? '0');
  const weights = lines.map((l) => ({ weight: weightOf(l), tieKey: l.costCenterCode }));
  const amounts = splitByWeight(toScaled(input.recognized), weights);
  const paid = splitByWeight(toScaled(input.paidNet), weights);
  const financial = splitByWeight(toScaled(input.financial), weights);
  return lines.flatMap<FpaRecognitionLine>((l, i) => {
    const people = l.employees?.length ? l.employees : null;
    if (!people) {
      return [
        {
          legalEntityId: l.legalEntityId,
          costCenterId: l.costCenterId,
          employeeId: null,
          amount: fromScaled(amounts[i]!),
          paidNet: fromScaled(paid[i]!),
          withheld: fromScaled(amounts[i]! - paid[i]!),
          financial: fromScaled(financial[i]!),
        },
      ];
    }
    const shares = people.map((p) => ({ weight: toScaled(p.amount), tieKey: p.employeeId }));
    const personAmount = splitByWeight(amounts[i]!, shares);
    const personPaid = splitByWeight(paid[i]!, shares);
    const personFinancial = splitByWeight(financial[i]!, shares);
    return people.map((p, j) => ({
      legalEntityId: l.legalEntityId,
      costCenterId: l.costCenterId,
      employeeId: p.employeeId,
      amount: fromScaled(personAmount[j]!),
      paidNet: fromScaled(personPaid[j]!),
      withheld: fromScaled(personAmount[j]! - personPaid[j]!),
      financial: fromScaled(personFinancial[j]!),
    }));
  });
}

/**
 * Situação do mês para quem lê o Realizado: "partial" enquanto houver título por pagar ou item
 * aguardando conferência, "registered" quando nada está em aberto. Nunca quer dizer fechamento
 * contábil: o livro continua no NetSuite.
 */
export function actualPeriodState(p: { pendingItems: number; openTitles: number }) {
  return p.pendingItems > 0 || p.openTitles > 0 ? ('partial' as const) : ('registered' as const);
}

const money = z.string().regex(/^\d+(\.\d{1,4})?$/);

export const fpaQueueListQuery = z.object({
  status: z.enum(FPA_QUEUE_STATUSES).optional(),
  legalEntityId: z.uuid().optional(),
  competence: z
    .string()
    .regex(/^\d{4}-\d{2}-01$/)
    .optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
  offset: z.coerce.number().int().min(0).default(0),
});

export const fpaQueueRegisterInput = z.object({
  // As três conferências são obrigatórias: o botão só existe com elas marcadas.
  checks: z.object({
    competence: z.literal(true),
    classification: z.literal(true),
    allocation: z.literal(true),
  }),
  note: z.string().trim().min(3).max(1000).optional(),
  settlements: z
    .array(z.object({ withholdingId: z.uuid(), amount: money }))
    .max(50)
    .default([]),
});

export const fpaQueueNoteInput = z.object({ note: z.string().trim().min(3).max(1000) });

/**
 * Validação da Controladoria do título provisório no Master (ADR 0094; 0436). Só registro: não
 * trava o pagamento nem o Registrar, e não é a confirmação do gestor. Divergente pede observação.
 */
export const FPA_PROVISIONAL_VALIDATION_RESULTS = ['validated', 'divergent'] as const;
export type FpaProvisionalValidationResult = (typeof FPA_PROVISIONAL_VALIDATION_RESULTS)[number];

export const fpaQueueProvisionalValidationInput = z
  .object({
    result: z.enum(FPA_PROVISIONAL_VALIDATION_RESULTS),
    note: z.string().trim().min(3).max(1000).optional(),
  })
  .refine((v) => v.result !== 'divergent' || !!v.note, {
    path: ['note'],
    message: 'Informe a observação da divergência',
  });

export const fpaOriginInput = z.object({
  code: z.string().regex(/^[A-Z0-9][A-Z0-9_-]{1,29}$/),
  name: z.string().trim().min(2).max(120),
  channel: z.enum(FPA_ACTUAL_CHANNELS),
  ownerLabel: z.string().trim().min(2).max(200),
  notes: z.string().trim().min(1).max(1000).nullish(),
});

export const fpaOriginUpdateInput = fpaOriginInput
  .omit({ code: true, channel: true })
  .extend({ isActive: z.boolean() });

export const fpaOriginRuleInput = z.object({
  originId: z.uuid(),
  expenseTypeId: z.uuid(),
  classificationId: z.uuid().nullish(),
  resultEffect: z.enum(FPA_RESULT_EFFECTS).default('result'),
  notes: z.string().trim().min(1).max(1000).nullish(),
});

export const fpaCutoverInput = z.object({
  legalEntityId: z.uuid(),
  firstMonth: z.string().regex(/^\d{4}-\d{2}-01$/),
  reason: z.string().trim().min(3).max(1000),
});
